/**
 * DatabaseConnectionsPage Component
 *
 * Main page for managing database connections with search functionality.
 */

import React, { useState, useMemo } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { DashboardLayout } from '../../../components/layout/DashboardLayout';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { Alert } from '../../../components/ui/Alert';
import { DatabaseConnectionTable } from '../components/DatabaseConnectionTable';
import { CreateDatabaseConnectionModal } from '../components/CreateDatabaseConnectionModal';
import { UpdateDatabaseConnectionModal } from '../components/UpdateDatabaseConnectionModal';
import { DatabaseConnectionDetailsModal } from '../components/DatabaseConnectionDetailsModal';
import { DeleteDatabaseConnectionModal } from '../components/DeleteDatabaseConnectionModal';
import { useDatabaseConnections } from '../hooks/useDatabaseConnections';
import { useAuth } from '../../../contexts/AuthContext';
import { hasPermission } from '../../../lib/utils/permissions';
import { useLayoutProps } from '../../../hooks/useLayoutProps';
import type { DatabaseConnection, DatabaseEngine, ConnectionType } from '../../../types/database-connection.types';
import { getErrorMessage } from '../../../lib/utils/errors';

/**
 * DatabaseConnectionsPage component
 */
export const DatabaseConnectionsPage: React.FC = () => {
  const { tenant, activeRole } = useAuth();
  const layoutProps = useLayoutProps();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [engineFilter, setEngineFilter] = useState<string>('');
  const [connectionTypeFilter, setConnectionTypeFilter] = useState<string>('');
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [selectedConnection, setSelectedConnection] = useState<DatabaseConnection | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const tenantId = tenant?.tenantId || '';

  // Permission checks
  const canCreate = hasPermission(activeRole?.permissions, 'resources:database-connection:create');
  const canUpdate = hasPermission(activeRole?.permissions, 'resources:database-connection:update');
  const canDelete = hasPermission(activeRole?.permissions, 'resources:database-connection:delete');

  // Debounced search (300ms delay)
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch database connections
  const {
    data: connectionsData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useDatabaseConnections(tenantId, {
    search: debouncedSearch || undefined,
    engine: engineFilter as DatabaseEngine || undefined,
    connectionType: connectionTypeFilter as ConnectionType || undefined,
  });

  const databaseConnections = connectionsData || [];

  // Filter database connections locally (additional filtering if needed)
  const filteredConnections = useMemo(() => {
    if (!databaseConnections) return [];

    let filtered = [...databaseConnections];

    // Search filter
    if (debouncedSearch) {
      const query = debouncedSearch.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.dbConnectionName.toLowerCase().includes(query) ||
          item.dbConnectionCode.toLowerCase().includes(query) ||
          (item.host && item.host.toLowerCase().includes(query)) ||
          (item.description && item.description.toLowerCase().includes(query))
      );
    }

    // Engine filter
    if (engineFilter) {
      filtered = filtered.filter((item) => item.engine === engineFilter);
    }

    // Connection type filter
    if (connectionTypeFilter) {
      filtered = filtered.filter((item) => item.connectionType === connectionTypeFilter);
    }

    return filtered;
  }, [databaseConnections, debouncedSearch, engineFilter, connectionTypeFilter]);

  const handleViewDetails = (connection: DatabaseConnection) => {
    setSelectedConnection(connection);
    setShowDetailsModal(true);
  };

  const handleEdit = (connection: DatabaseConnection) => {
    setSelectedConnection(connection);
    setShowUpdateModal(true);
  };

  const handleDelete = (connection: DatabaseConnection) => {
    setSelectedConnection(connection);
    setShowDeleteModal(true);
  };

  return (
    <DashboardLayout {...layoutProps}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Database Connections</h1>
            <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              Manage external database connections and application access
            </p>
          </div>
          {canCreate && (
            <Button onClick={() => setShowCreateModal(true)}>
              Create Database Connection
            </Button>
          )}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <Input
              type="text"
              placeholder="Search database connections..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="w-48">
            <Select
              value={engineFilter}
              onChange={(e) => setEngineFilter(e.target.value)}
            >
              <option value="">All Engines</option>
              <option value="SQL_SERVER">SQL Server</option>
              <option value="MYSQL">MySQL</option>
              <option value="POSTGRESQL">PostgreSQL</option>
              <option value="ORACLE">Oracle</option>
            </Select>
          </div>
          <div className="w-48">
            <Select
              value={connectionTypeFilter}
              onChange={(e) => setConnectionTypeFilter(e.target.value)}
            >
              <option value="">All Types</option>
              <option value="SELF_MANAGED">Self-Managed</option>
              <option value="KETRICS_MANAGED">Ketrics-Managed</option>
            </Select>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <ArrowPathIcon className={`h-4 w-4 mr-1 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="error">
            {getErrorMessage(error)}
          </Alert>
        )}

        {/* Database Connections Table */}
        <DatabaseConnectionTable
          databaseConnections={filteredConnections}
          isLoading={isLoading}
          onView={handleViewDetails}
          onEdit={handleEdit}
          onDelete={handleDelete}
          canEdit={canUpdate}
          canDelete={canDelete}
        />

        {/* Modals */}
        <CreateDatabaseConnectionModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          tenantId={tenantId}
        />

        {showDetailsModal && selectedConnection && (
          <DatabaseConnectionDetailsModal
            isOpen={showDetailsModal}
            onClose={() => {
              setShowDetailsModal(false);
              setSelectedConnection(null);
            }}
            tenantId={tenantId}
            dbConnectionId={selectedConnection.dbConnectionId}
          />
        )}

        {showUpdateModal && selectedConnection && (
          <UpdateDatabaseConnectionModal
            isOpen={showUpdateModal}
            onClose={() => {
              setShowUpdateModal(false);
              setSelectedConnection(null);
            }}
            tenantId={tenantId}
            databaseConnection={selectedConnection}
          />
        )}

        {showDeleteModal && selectedConnection && (
          <DeleteDatabaseConnectionModal
            isOpen={showDeleteModal}
            onClose={() => {
              setShowDeleteModal(false);
              setSelectedConnection(null);
            }}
            databaseConnection={selectedConnection}
          />
        )}
      </div>
    </DashboardLayout>
  );
};
